/**
 * Campaign CLEARED reveal — text rewards + optional cosmetic-on-cursor preview.
 */

import {
  COSMETIC_ART_SIZE,
  COSMETIC_REF_CURSOR_R,
  drawCursorWithCosmeticAsync,
} from "../cursor-cosmetics";
import { startCosmeticPreviewLoop, stopCosmeticPreviewLoop } from "../cosmetic-preview-loop";
import { ANIM_FRAME_MS } from "../shop-creator-fx";

export type CampaignRewardRevealCosmetic = {
  name?: string | null;
  imageData: string;
  animationFrames?: string[] | null;
  newCursor?: boolean;
};

function escapeHtml(s: string) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function readCursorColor(): string {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue("--cursor-color").trim();
    if (v) return v;
  } catch {
    /* ignore */
  }
  return "#66ffff";
}

export function showCampaignRewardReveal(opts: {
  lines: string[];
  cosmetic?: CampaignRewardRevealCosmetic | null;
  holdMs?: number;
}): Promise<void> {
  const holdMs = Math.max(1200, opts.holdMs ?? 2400);
  const overlay = document.createElement("div");
  overlay.className = "campaignRewardRevealOverlay";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-live", "polite");

  const card = document.createElement("div");
  card.className = "campaignRewardRevealCard";

  const text = document.createElement("div");
  text.className = "campaignRewardRevealText";
  text.innerHTML = opts.lines
    .map(
      (l, i) =>
        `<div class="challengeRevealLine${i === 0 ? " challengeRevealLine--title" : ""}">${escapeHtml(l)}</div>`,
    )
    .join("");
  card.appendChild(text);

  let previewLoopCanvas: HTMLCanvasElement | null = null;
  const cos = opts.cosmetic;
  if (cos?.imageData) {
    const previewWrap = document.createElement("div");
    previewWrap.className = "campaignRewardRevealPreview";
    if (cos.name) {
      const nameEl = document.createElement("div");
      nameEl.className = "campaignRewardRevealItemName";
      nameEl.textContent = cos.name;
      previewWrap.appendChild(nameEl);
    }
    const canvas = document.createElement("canvas");
    canvas.className = "campaignRewardRevealCanvas";
    canvas.width = 220;
    canvas.height = 220;
    previewWrap.appendChild(canvas);
    card.appendChild(previewWrap);
    previewLoopCanvas = canvas;

    const paint = () => {
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const cx = canvas.width * 0.5;
      const cy = canvas.height * 0.5;
      const fill = Math.min(canvas.width, canvas.height) * 0.72;
      const r = fill / (COSMETIC_ART_SIZE / COSMETIC_REF_CURSOR_R);
      let frame = cos.imageData;
      if (cos.animationFrames?.length) {
        const fi = Math.floor(performance.now() / ANIM_FRAME_MS) % cos.animationFrames.length;
        frame = cos.animationFrames[fi] || cos.imageData;
      }
      void drawCursorWithCosmeticAsync(ctx, cx, cy, r, readCursorColor(), frame, {
        cosmeticOnly: !!cos.newCursor,
        animationFrames: null,
      });
    };
    paint();
    startCosmeticPreviewLoop(canvas, paint, () => document.body.contains(overlay));
  }

  overlay.appendChild(card);
  document.body.appendChild(overlay);
  requestAnimationFrame(() => overlay.classList.add("campaignRewardRevealOverlay--on"));

  return new Promise((resolve) => {
    window.setTimeout(() => {
      if (previewLoopCanvas) stopCosmeticPreviewLoop(previewLoopCanvas);
      overlay.classList.remove("campaignRewardRevealOverlay--on");
      window.setTimeout(() => {
        overlay.remove();
        resolve();
      }, 220);
    }, holdMs);
  });
}
